import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Zap, Target, Eye, ShieldCheck, Users, Rocket,
  Cpu, Sparkles, MapPin, Mail,
} from 'lucide-react';
import { ABOUT_PAGE, REGIONS, CONTACT } from '../mock';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';

const ICONS = { Target, Eye, ShieldCheck, Users, Rocket, Zap, Cpu, Sparkles };
const REGION_COLOR = {
  lime: 'text-[#5bd0ff] border-[#5bd0ff]/40',
  cyan: 'text-[#0ef3fe] border-[#0ef3fe]/40',
  magenta: 'text-[#088ffe] border-[#088ffe]/40',
};

export default function About() {
  const { hero, story, mv, values, divisions } = ABOUT_PAGE;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'About Us — Electronico Holdings';
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 overflow-hidden grid-bg">
        <div className="blob bg-[#0ef3fe] w-[460px] h-[460px] -top-24 -left-24 animate-pulse-glow" />
        <div className="blob bg-[#0a6bd6] w-[360px] h-[360px] top-20 right-0 opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full neon-border px-4 py-1.5 mb-6">
              <Sparkles className="h-4 w-4 text-[#0ef3fe]" />
              <span className="font-mono-accent text-xs text-white/80">{hero.kicker}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.08] mb-6">
              One Group. <span className="gradient-text animate-gradient">Three Powerhouses.</span>
            </h1>
            <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">{hero.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <a href="#story" className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(14,243,254,0.5)] transition-shadow">
                Our Story <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Home
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 max-w-lg">
              {hero.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl md:text-3xl font-bold text-glow-cyan">{s.value}</div>
                  <div className="text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-floaty">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#0ef3fe]/30 to-[#0a6bd6]/30 blur-2xl rounded-3xl" />
            <div className="relative rounded-3xl overflow-hidden neon-border">
              <img src={hero.image} alt="Electronico team" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono-accent text-xs text-white">// EST. HERMANUS</span>
                <span className="flex items-center gap-2 text-xs text-white/70">
                  <MapPin className="h-3.5 w-3.5 text-[#5bd0ff]" /> Western Cape
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="relative py-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-mono-accent text-xs text-[#088ffe]">// OUR STORY</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-8">Who We Are</h2>
          <div className="space-y-5">
            {story.map((p, i) => (
              <p key={i} className={`leading-relaxed ${i === 0 ? 'text-xl text-white/85 font-display' : 'text-white/60'}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative py-16 px-5 grid-bg">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {mv.map((m) => {
            const Icon = ICONS[m.icon];
            return (
              <div key={m.title} className="neon-border rounded-3xl p-8 md:p-10 hover:border-[#0ef3fe]/40 transition-colors">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#0ef3fe]/20 to-[#0a6bd6]/20 flex items-center justify-center mb-5">
                  <Icon className="h-7 w-7 text-[#0ef3fe]" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-3">{m.title}</h3>
                <p className="text-white/60 leading-relaxed">{m.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#5bd0ff]">// WHAT DRIVES US</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Our Core Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="group h-full overflow-hidden neon-border rounded-3xl hover:-translate-y-2 transition-transform duration-300">
                  <div className="relative h-40 overflow-hidden">
                    <img src={v.image} alt={v.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/40 to-transparent" />
                    <span className="absolute bottom-3 left-4 h-12 w-12 rounded-2xl bg-[#0a1526]/70 backdrop-blur-md flex items-center justify-center group-hover:glow-cyan transition-shadow">
                      <Icon className="h-6 w-6 text-[#0ef3fe]" />
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold mb-2">{v.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="relative py-20 px-5 grid-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0a6bd6]">// OUR DIVISIONS</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Three Divisions, One Standard</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {divisions.map((d) => {
              const Icon = ICONS[d.icon];
              return (
                <Link key={d.to} to={d.to} className="group relative block overflow-hidden neon-border rounded-3xl h-72 hover:-translate-y-2 transition-all duration-300">
                  <img src={d.image} alt={d.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/50 to-transparent" />
                  <span className="absolute top-5 left-5 flex h-11 w-11 items-center justify-center rounded-xl backdrop-blur-md" style={{ backgroundColor: `${d.color}26` }}>
                    <Icon className="h-6 w-6" style={{ color: d.color }} />
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                    <h3 className="font-display text-xl font-bold max-w-[75%] leading-tight">{d.label}</h3>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 group-hover:bg-white/10 transition-colors" style={{ color: d.color }}>
                      <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE */}
      <section className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0ef3fe]">// GLOBAL PRESENCE</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Trusted Across Continents</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {REGIONS.map((r, i) => (
              <div key={r.name} className={`group h-full overflow-hidden neon-border rounded-3xl border ${REGION_COLOR[r.color]} hover:bg-white/[0.03] transition-colors`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/30 to-transparent" />
                  <span className="absolute top-4 left-5 font-mono-accent text-5xl font-bold text-white/25">0{i + 1}</span>
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl font-bold mb-2 text-white">{r.name}</h3>
                  <p className="text-sm text-white/55 mb-6 leading-relaxed">{r.desc}</p>
                  <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold ${REGION_COLOR[r.color]}`}>
                    {r.metric}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-5 grid-bg">
        <div className="max-w-5xl mx-auto">
          <div className="relative neon-border rounded-3xl p-10 md:p-16 overflow-hidden text-center">
            <div className="blob bg-[#0ef3fe] w-80 h-80 -top-20 -left-10 opacity-30" />
            <div className="blob bg-[#0a6bd6] w-80 h-80 -bottom-20 -right-10 opacity-30" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Let's Build Something Together</h2>
              <p className="text-white/60 max-w-xl mx-auto mb-8">
                Whether you need to secure, manufacture or market — our team in Hermanus is ready to help.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`mailto:${CONTACT.email}`} className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(14,243,254,0.5)] transition-shadow">
                  <Mail className="h-5 w-5" /> Get in Touch
                </a>
                <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Back Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
